import { storage } from 'lib/storage';
import { Asset } from 'store/assets/types';
import { Organisation } from 'store/organisations/types';

import {
  ASSET_FILE_PREFIX,
  ASSET_PREFIX,
  FILE_PREFIX,
  ORGANISATION_PREFIX,
  THUMBNAIL_PREFIX,
} from 'store/sync/constants';

const getPropertyDataFileLocation = (
  assetId: string,
): {
  directory: string;
  filename: string;
} => ({ directory: assetId, filename: `${ASSET_PREFIX}${assetId}` });

const getOrganisationDataFileLocation = (
  organisationId: string,
): {
  directory: string;
  filename: string;
} => ({ directory: ORGANISATION_PREFIX, filename: organisationId });

const getPendingFileUploadLocation = (
  fileId: string,
): {
  directory: string;
  filename: string;
} => ({
  directory: 'files',
  filename: `${FILE_PREFIX}${fileId}`,
});

const formatPropertyThumbnailLocation = (
  assetId: string,
  fileId: string,
): {
  directory: string;
  filename: string;
} => ({ directory: assetId, filename: `${THUMBNAIL_PREFIX}${fileId}` });

const formatPropertyFileLocation = (
  assetId: string,
  fileId: string,
): {
  directory: string;
  filename: string;
} => ({ directory: assetId, filename: `${ASSET_FILE_PREFIX}${fileId}` });

export const clearStorage = async (): Promise<void> =>
  await storage.deleteAll();

export const setOrganisationData = async (
  organisationId: string,
  data: Organisation,
): Promise<void> =>
  await storage.writeJsonFile<Organisation>({
    ...getOrganisationDataFileLocation(organisationId),
    data: data,
  });

export const getOrganisationData = async (
  organisationId: string,
): Promise<Organisation> =>
  await storage.readJsonFile<Organisation>(
    getOrganisationDataFileLocation(organisationId),
  );

export const removeOrganisationData = async (
  organisationId: string,
): Promise<void> =>
  await storage.deleteFile(getOrganisationDataFileLocation(organisationId));

export const setAssetData = async (
  assetId: string,
  data: Asset,
): Promise<void> =>
  await storage.writeJsonFile<Asset>({
    ...getPropertyDataFileLocation(assetId),
    data: data,
  });

export const getAssetData = async (assetId: string): Promise<Asset> =>
  await storage.readJsonFile<Asset>(getPropertyDataFileLocation(assetId));

export const removeAssetDirectory = async (assetId: string): Promise<void> =>
  await storage.deleteDirectory(getPropertyDataFileLocation(assetId).directory);

// Downloaded Property Files

export const getAssetFileInline = async (
  assetId: string,
  fileId: string,
  base64Prefix?: string,
): Promise<string> =>
  await storage.getBase64Inline({
    ...formatPropertyFileLocation(assetId, fileId),
    base64Prefix: base64Prefix || '',
  });

export const setAssetFile = async (
  assetId: string,
  fileId: string,
  base64Data: string,
): Promise<void> =>
  await storage.writeBase64File({
    ...formatPropertyFileLocation(assetId, fileId),
    data: base64Data,
  });

export const removeAssetFile = async (
  assetId: string,
  fileId: string,
): Promise<void> =>
  await storage.deleteBase64File(formatPropertyFileLocation(assetId, fileId));

// Downloaded Property Thumbnails

export const setAssetThumbnail = async (
  assetId: string,
  thumbnailId: string,
  base64Data: string,
): Promise<void> =>
  await storage.writeBase64File({
    ...formatPropertyThumbnailLocation(assetId, thumbnailId),
    data: base64Data,
  });

export const getAssetThumbnailInline = async (
  assetId: string,
  thumbnailId: string,
): Promise<string> =>
  await storage.getBase64Inline({
    ...formatPropertyThumbnailLocation(assetId, thumbnailId),
    base64Prefix: 'data:image/jpeg;base64,',
  });

export const removeAssetThumbnail = async (
  assetId: string,
  thumbnailId: string,
): Promise<void> =>
  await storage.deleteBase64File(
    formatPropertyThumbnailLocation(assetId, thumbnailId),
  );

// Locally Added Files

export const writeLocallyAddedFile = async (
  fileUuid: string,
  base64Data: string,
): Promise<void> =>
  await storage.writeBase64File({
    ...getPendingFileUploadLocation(fileUuid),
    data: base64Data,
  });

export const readLocallyAddedFile = async (fileUuid: string): Promise<string> =>
  await storage.readBase64File(getPendingFileUploadLocation(fileUuid));

export const deleteLocallyAddedFile = async (fileUuid: string): Promise<void> =>
  await storage.deleteBase64File(getPendingFileUploadLocation(fileUuid));

export const setAuthToken = async (token: string): Promise<void> => {
  await storage.setSecureValue('rbat', token);
};
export const getAuthToken = async (): Promise<string | null> => {
  return storage.getSecureValue('rbat');
};
export const deleteAuthToken = async (): Promise<void> => {
  storage.deleteSecureValue('rbat');
};
